import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  Grid,
  FormHelperText,
  Chip,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { shades } from '../../../theme';

// Certifications and Licenses Component
const CertificationsLicenses = ({ onValidityChange, onDataChange, initialData = {} }) => {
  const [certifications, setCertifications] = useState([{
    certificateName: '',
    file: null,
    fileName: '',
    issueDate: '',
    expiryDate: '',
    hasExpiry: true
  }]);
  const [errors, setErrors] = useState([{}]);
  const fileInputRefs = useRef([]);

  // Load saved data from localStorage when component mounts
  useEffect(() => {
    const storedData = localStorage.getItem('certificationsData');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        // We can't store the actual file objects in localStorage, so just initialize with null
        const processedData = data.map(cert => ({
          ...cert,
          file: null
        }));
        setCertifications(processedData);
        setErrors(Array(data.length).fill({}));
      } catch (e) {
        console.error('Error parsing certifications data from localStorage', e);
      }
    }
  }, []);

  // Save data to localStorage whenever any field changes, excluding the file object
  useEffect(() => {
    const dataToStore = certifications.map(cert => ({
      ...cert,
      file: null // Don't store the actual file object
    }));
    localStorage.setItem('certificationsData', JSON.stringify(dataToStore));
  }, []);

  // Validate form
  const validateForm = () => {
    let isValid = true;
    const newErrors = certifications.map(cert => {
      const certErrors = {};

      if (!cert.certificateName) {
        certErrors.certificateName = 'Certificate Name is required.';
        isValid = false;
      }

      if (!cert.fileName) {
        certErrors.file = 'Certificate file is required.';
        isValid = false;
      }

      if (!cert.issueDate) {
        certErrors.issueDate = 'Issue Date is required.';
        isValid = false;
      }

      if (cert.hasExpiry && !cert.expiryDate) {
        certErrors.expiryDate = 'Expiry Date is required if certificate expires.';
        isValid = false;
      }

      if (cert.issueDate && cert.expiryDate && new Date(cert.issueDate) > new Date(cert.expiryDate)) {
        certErrors.expiryDate = 'Expiry Date must be after Issue Date.';
        isValid = false;
      }

      return certErrors;
    });

    setErrors(newErrors);
    return isValid;
  };

  // Automatically check form validity whenever any field changes
  useEffect(() => {
    const isValid = validateForm();
    onValidityChange(isValid);
    if (isValid) {
      onDataChange(certifications);
    }
  }, [certifications, onValidityChange]);

  // Style configuration for text fields
  const textFieldStyle = {
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#9e9e9e'
      },
      '&:hover fieldset': {
        borderColor: '#1976d2'
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff !important',
        '& fieldset': {
          borderColor: '#1976d2'
        }
      }
    },
    '& .MuiInputLabel-root': {
      color: '#9e9e9e'
    },
    '& .MuiOutlinedInput-input': {
      color: shades.neutral[700]
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
      transition: 'background-color 5000s ease-in-out 0s'
    }
  };

  const addCertification = () => {
    setCertifications([...certifications, {
      certificateName: '',
      file: null,
      fileName: '',
      issueDate: '',
      expiryDate: '',
      hasExpiry: true
    }]);
    setErrors([...errors, {}]);

    // Ensure we have a reference for the new file input
    fileInputRefs.current = [...fileInputRefs.current, React.createRef()];
  };

  const removeCertification = (index) => {
    if (certifications.length <= 1) {
      alert('You must have at least one certification or license.');
      return;
    }

    const newCertifications = [...certifications];
    newCertifications.splice(index, 1);
    setCertifications(newCertifications);

    const newErrors = [...errors];
    newErrors.splice(index, 1);
    setErrors(newErrors);

    // Remove the reference as well
    fileInputRefs.current.splice(index, 1);
  };

  const handleChange = (index, field, value) => {
    const newCertifications = [...certifications];
    newCertifications[index][field] = value;

    // If hasExpiry is false, clear expiryDate
    if (field === 'hasExpiry' && value === false) {
      newCertifications[index].expiryDate = '';
    }

    setCertifications(newCertifications);

    // Clear error for this field if it has a value now
    if (value || (field === 'file' && value !== null)) {
      const newErrors = [...errors];
      if (newErrors[index] && newErrors[index][field]) {
        newErrors[index] = { ...newErrors[index], [field]: '' };
        setErrors(newErrors);
      }
    }
  };

  const handleFileChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setErrors(prevErrors => {
        const newErrors = [...prevErrors];
        newErrors[index] = { ...newErrors[index], file: 'Invalid file format. Please upload .jpg, .png, or .pdf files only.' };
        return newErrors;
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prevErrors => {
        const newErrors = [...prevErrors];
        newErrors[index] = { ...newErrors[index], file: 'File size exceeds 5MB limit.' };
        return newErrors;
      });
      return;
    }

    // Valid file, update state
    handleChange(index, 'file', file);
    handleChange(index, 'fileName', file.name);

    // Clear any existing file errors
    setErrors(prevErrors => {
      const newErrors = [...prevErrors];
      if (newErrors[index] && newErrors[index].file) {
        newErrors[index] = { ...newErrors[index], file: '' };
      }
      return newErrors;
    });
  };

  // Initialize refs array based on certifications array length
  useEffect(() => {
    fileInputRefs.current = Array(certifications.length).fill().map((_, i) => fileInputRefs.current[i] || React.createRef());
  }, [certifications.length]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, color: 'black', height: 'auto' }}>
      <Typography variant='h6' sx={{ color: shades.neutral[700], marginBottom: 1 }}>
        Certifications and Licenses
      </Typography>

      {certifications.map((cert, index) => (
        <Box
          key={index}
          sx={{
            border: `1px solid ${shades.neutral[300]}`,
            padding: 3,
            borderRadius: 1,
            position: 'relative',
            mb: 3
          }}
        >
          {/* Remove button - only show if there's more than one entry */}
          {certifications.length > 1 && (
            <IconButton
              onClick={() => removeCertification(index)}
              sx={{ position: 'absolute', top: 10, right: 10 }}
              color='error'
              size='small'
            >
              <DeleteIcon />
            </IconButton>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label='Certificate Name *'
                fullWidth
                value={cert.certificateName}
                onChange={(e) => handleChange(index, 'certificateName', e.target.value)}
                error={!!errors[index]?.certificateName}
                helperText={errors[index]?.certificateName}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12}>
              <input
                accept='.jpg,.jpeg,.png,.pdf'
                style={{ display: 'none' }}
                id={`certificate-file-${index}`}
                type='file'
                ref={el => fileInputRefs.current[index] = el}
                onChange={(e) => handleFileChange(index, e)}
              />
              <label htmlFor={`certificate-file-${index}`}>
                <Button
                  variant='outlined'
                  component='span'
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 1 }}
                >
                  Upload Certificate *
                </Button>
              </label>
              {cert.fileName && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Chip
                    label={cert.fileName}
                    onDelete={() => {
                      handleChange(index, 'file', null);
                      handleChange(index, 'fileName', '');
                    }}
                    sx={{ maxWidth: '100%', overflow: 'hidden' }}
                  />
                </Box>
              )}
              {errors[index]?.file && (
                <FormHelperText error>{errors[index]?.file}</FormHelperText>
              )}
              <FormHelperText>
                Accepted formats: .jpg, .png, .pdf (max 5MB)
              </FormHelperText>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label='Issue Date *'
                type='date'
                fullWidth
                value={cert.issueDate}
                onChange={(e) => handleChange(index, 'issueDate', e.target.value)}
                error={!!errors[index]?.issueDate}
                helperText={errors[index]?.issueDate}
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cert.hasExpiry}
                    onChange={(e) => handleChange(index, 'hasExpiry', e.target.checked)}
                  />
                }
                label='Has Expiry Date'
              />
            </Grid>

            {cert.hasExpiry && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Expiry Date *'
                  type='date'
                  fullWidth
                  value={cert.expiryDate}
                  onChange={(e) => handleChange(index, 'expiryDate', e.target.value)}
                  error={!!errors[index]?.expiryDate}
                  helperText={errors[index]?.expiryDate}
                  InputLabelProps={{ shrink: true }}
                  sx={textFieldStyle}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      ))}

      <Button
        variant='outlined'
        startIcon={<AddIcon />}
        onClick={addCertification}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add Another Certification
      </Button>
    </Box>
  );
};

export default CertificationsLicenses;
